import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "@helpers/translate";
import "./index.sass";

const CryptoMarketCapBreakdown = ({ chartConfig }) => {
  const [activePoint, setActivePoint] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState(null);
  const { __ } = useTranslation();

  const handleMouseMove = (event) => {
    const pointsData = chartConfig?.series?.[0]?.pointsData || [];

    if (!pointsData.length) return;

    const rect = event.currentTarget.getBoundingClientRect();

    const mouseX =
      ((event.clientX - rect.left) / rect.width) * chartConfig.chartWidth;

    const nearestPoint = pointsData.reduce((closestPoint, point) => {
      const closestDistance = Math.abs(closestPoint.x - mouseX);
      const currentDistance = Math.abs(point.x - mouseX);

      return currentDistance < closestDistance ? point : closestPoint;
    }, pointsData[0]);

    setActivePoint(nearestPoint);
    setTooltipPosition({
      left: event.clientX,
      top: event.clientY,
    });
  };

  const handleMouseLeave = () => {
    setActivePoint(null);
    setTooltipPosition(null);
  };

  return (
    <div className="crypto-market-cap-breakdown">
      <div className="crypto-market-cap-breakdown__y-axis">
        {chartConfig?.yAxisLabels?.map((label) => (
          <span key={label} className="crypto-market-cap-breakdown__y-label">
            {label}
          </span>
        ))}
      </div>

      <div className="crypto-market-cap-breakdown__chart-container">
        <svg
          viewBox={`0 0 ${chartConfig?.chartWidth || 500} ${
            chartConfig?.chartHeight || 200
          }`}
          preserveAspectRatio="none"
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          <defs>
            {chartConfig?.series?.map((item) => (
              <linearGradient
                key={item.name}
                id={`breakdownAreaGradient${item.name}`}
                x1="0%"
                y1="0%"
                x2="0%"
                y2="100%"
              >
                <stop offset="0%" stopColor={item.color} stopOpacity="0.24" />
                <stop offset="60%" stopColor={item.color} stopOpacity="0.08" />
                <stop offset="100%" stopColor={item.color} stopOpacity="0" />
              </linearGradient>
            ))}
          </defs>

          {chartConfig?.series?.map((item) => (
            <polygon
              key={`${item.name}-area`}
              points={item.areaPoints}
              fill={`url(#breakdownAreaGradient${item.name})`}
            />
          ))}

          {chartConfig?.series?.map((item) => (
            <polyline
              key={`${item.name}-line`}
              points={item.points}
              fill="none"
              stroke={item.color}
              strokeWidth="2"
              vectorEffect="non-scaling-stroke"
            />
          ))}

          {activePoint && (
            <line
              x1={activePoint.x}
              y1="0"
              x2={activePoint.x}
              y2={chartConfig?.chartHeight || 200}
              stroke="rgba(255, 255, 255, 0.3)"
              strokeWidth="1"
              vectorEffect="non-scaling-stroke"
            />
          )}

          {activePoint &&
            chartConfig?.series?.map((item) => {
              const point = item.pointsData?.find(
                (seriesPoint) => seriesPoint.date === activePoint.date,
              );

              if (!point) return null;

              return (
                <ellipse
                  key={`${item.name}-active-point`}
                  cx={point.x}
                  cy={point.y}
                  rx="1"
                  ry="3"
                  fill={item.color}
                  stroke="#ffffff"
                  strokeWidth="2"
                  vectorEffect="non-scaling-stroke"
                />
              );
            })}
        </svg>

        <div className="crypto-market-cap-breakdown__x-axis">
          {chartConfig?.xAxisLabels?.map((label, index) => (
            <span
              key={`${label}-${index}`}
              className="crypto-market-cap-breakdown__x-label"
            >
              {label}
            </span>
          ))}
        </div>
      </div>

      {activePoint &&
        tooltipPosition &&
        createPortal(
          <div
            className="crypto-market-cap-breakdown__tooltip"
            style={{
              left: tooltipPosition.left,
              top: tooltipPosition.top,
            }}
          >
            <span className="crypto-market-cap-breakdown__tooltip-date">
              {activePoint.tooltipDate}
            </span>

            <span className="crypto-market-cap-breakdown__tooltip-row">
              <span
                className="crypto-market-cap-breakdown__tooltip-dot"
                style={{ backgroundColor: "#f59e0b" }}
              />
              Bitcoin: {activePoint.bitcoin}
            </span>

            <span className="crypto-market-cap-breakdown__tooltip-row">
              <span
                className="crypto-market-cap-breakdown__tooltip-dot"
                style={{ backgroundColor: "#6366f1" }}
              />
              Ethereum: {activePoint.ethereum}
            </span>

            <span className="crypto-market-cap-breakdown__tooltip-row">
              <span
                className="crypto-market-cap-breakdown__tooltip-dot"
                style={{ backgroundColor: "#10b981" }}
              />
              {__("markets.cryptoMarketCapOthers")}: {activePoint.others}
            </span>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default CryptoMarketCapBreakdown;
